import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { LESSON_NOTE_LIMIT } from '../../hooks/useLessonNotes';
import { isDesktopLocalProfile } from '../../utils/desktopProfile';
import { SignInToTrackLink } from '../LearningStatus/LearningStatus';
import styles from './lessonNote.module.scss';

interface Props {
    lessonId: string;
    lessonLabel?: string;
    note?: string;
    ready: boolean;
    saving: boolean;
    loadError: boolean;
    retry: () => void;
    onSave: (lessonId: string, text: string) => Promise<boolean>;
}

export default function LessonNote({ lessonId, lessonLabel, note, ready, saving, loadError, retry, onSave }: Props) {
    const { t } = useTranslation('common');
    const { status } = useAuth();
    const id = useId();
    const [mode, setMode] = useState<'collapsed' | 'view' | 'edit' | 'delete'>('collapsed');
    const [draft, setDraft] = useState('');
    const [error, setError] = useState('');
    const edit = () => { setDraft(note || ''); setError(''); setMode('edit'); };
    const save = async (deleting = false) => {
        setError('');
        const success = await onSave(lessonId, deleting ? '' : draft);
        if (success) setMode(deleting ? 'collapsed' : 'view');
        else setError(deleting ? t('lessonNote.deleteError') : t('lessonNote.saveError'));
    };

    return (
        <section id={id} className={styles.section} aria-label={t('lessonNote.regionLabel', { lesson: lessonLabel ?? lessonId })}>
            {status === 'unauthenticated' ? <SignInToTrackLink label={t('lessonNote.signInToAdd')} /> : loadError ? (
                <div>
                    <p role="alert">{t('lessonNote.loadError')}</p>
                    <button type="button" className={styles.button} onClick={retry}>{t('lessonNote.retry')}</button>
                </div>
            ) : mode === 'collapsed' ? (
                <button type="button" className={styles.button} disabled={!ready || saving}
                    aria-expanded={false} aria-controls={id} onClick={note ? () => setMode('view') : edit}>
                    {!ready ? t('lessonNote.loading') : note ? t('lessonNote.seeNotes') : t('lessonNote.addNote')}
                </button>
            ) : (
                <div>
                    {mode === 'edit' ? (
                        <form onSubmit={event => { event.preventDefault(); void save(); }}>
                            <label htmlFor={`${id}-text`} className={styles.label}>{t('lessonNote.yourNote')}</label>
                            <textarea id={`${id}-text`} className={styles.input} value={draft} autoFocus
                                maxLength={LESSON_NOTE_LIMIT} rows={5} disabled={saving}
                                onChange={event => setDraft(event.target.value)} aria-describedby={`${id}-limit`} />
                            <p id={`${id}-limit`} className={styles.hint}>{t(isDesktopLocalProfile ? 'lessonNote.limitHintLocal' : 'lessonNote.limitHint', { count: draft.length, limit: LESSON_NOTE_LIMIT })}</p>
                            <div className={styles.actions}>
                                <button type="submit" className={styles.button} disabled={saving || !draft.trim()}>{saving ? t('lessonNote.saving') : t('lessonNote.save')}</button>
                                <button type="button" className={styles.button} disabled={saving} onClick={() => { setMode(note ? 'view' : 'collapsed'); setError(''); }}>{t('lessonNote.cancel')}</button>
                            </div>
                        </form>
                    ) : (
                        <>
                            <h3 className={styles.label}>{t('lessonNote.yourNote')}</h3>
                            <p className={styles.note}>{note}</p>
                            {mode === 'delete' ? (
                                <>
                                    <p>{t('lessonNote.deleteConfirmPrompt')}</p>
                                    <div className={styles.actions}>
                                        <button type="button" className={styles.button} disabled={saving} onClick={() => void save(true)}>{saving ? t('lessonNote.deleting') : t('lessonNote.confirmDelete')}</button>
                                        <button type="button" className={styles.button} disabled={saving} onClick={() => { setMode('view'); setError(''); }}>{t('lessonNote.keep')}</button>
                                    </div>
                                </>
                            ) : (
                                <div className={styles.actions}>
                                    <button type="button" className={styles.button} disabled={saving} onClick={edit}>{t('lessonNote.edit')}</button>
                                    <button type="button" className={styles.button} disabled={saving} onClick={() => setMode('delete')}>{t('lessonNote.delete')}</button>
                                    <button type="button" className={styles.button} aria-expanded={true} aria-controls={id} onClick={() => { setMode('collapsed'); setError(''); }}>{t('lessonNote.hide')}</button>
                                </div>
                            )}
                        </>
                    )}
                    {error && <p role="alert">{error}</p>}
                </div>
            )}
        </section>
    );
}
